/**
 * RecordingGuard — crash-safe in-progress recordings for Timbrica tools.
 *
 * THE PROBLEM
 *   A browser MediaRecorder capture lives only in the tab. Navigate away, close
 *   the tab, or crash mid-recording and the take is gone — the MediaStream and
 *   the JS heap die with the page. A recording literally cannot "continue" after
 *   navigation (the OS releases the mic/camera). What we CAN do is make sure the
 *   user doesn't silently LOSE it:
 *     1. warn before an accidental unload while recording, and
 *     2. stream the recorder's chunks to IndexedDB as they arrive, so the next
 *        visit can recover and hand back what was captured.
 *
 * WHY THIS DESIGN (empirically chosen, not guessed)
 *   tests/playwright/recording-guard-variant-bench-scratch.mjs compared two
 *   persistence strategies in a real Chromium against a HARD mid-recording
 *   navigation:
 *     B1  append-per-chunk   — each timeslice chunk = its own IDB record
 *     B2  whole-blob rewrite — re-concat + overwrite one record every chunk
 *   Both RECOVERED and DECODED correctly (the key uncertainty — webm/opus keeps
 *   its header only in chunk 0 — was disproven: concatenating separately-stored
 *   timeslice chunks in order decodes fine). But B2 wrote 6× the bytes at 12 s
 *   and grows O(n²) with length; B1 is O(n) and even caught a slightly longer
 *   tail. RecordingGuard therefore uses B1 (the interview-studio pattern).
 *
 * USAGE — minimal (zero-config recovery: a "recovered unfinished recording"
 * toast with Download / Discard shows up automatically on the next visit):
 *
 *   const guard = new RecordingGuard({ slug: 'multi-track' });
 *   // when the user starts recording:
 *   guard.begin({ mimeType: rec.mimeType, label: 'Take 1' });
 *   rec.ondataavailable = e => { if (e.data && e.data.size) guard.pushChunk(e.data); };
 *   rec.start(guard.timesliceMs);        // guard suggests 1000 ms
 *   guard.attachRecorder(rec);           // lets the guard flush the tail on hide
 *   // once the finished take is safely in hand (blob built / saved to history):
 *   guard.finish();                      // clears the session — no longer "unfinished"
 *
 * USAGE — rich (load the recovered take straight back into the tool UI):
 *
 *   const guard = new RecordingGuard({
 *     slug: 'multi-track',
 *     onRecover(session) {
 *       // session = { id, blob, meta, sizeBytes, ageMs, accept(), discard() }
 *       showMyRestoreBar(session);       // call session.accept()/discard() from buttons
 *       return true;                     // returning true suppresses the default toast
 *     }
 *   });
 *
 * API
 *   new RecordingGuard(opts)   opts: { slug*, onRecover?, warnOnUnload=true,
 *                                      timesliceMs=1000, maxAgeMs=7d, autoRecover=true }
 *   guard.begin(meta)          start a session (meta free-form; mimeType used for recovery blob)
 *   guard.pushChunk(blob)      persist one timeslice chunk (call from ondataavailable)
 *   guard.attachRecorder(mr)   optional: guard calls mr.requestData() on tab-hide to flush the tail
 *   guard.finish()             session completed cleanly → delete it
 *   guard.discardActive()      user cancelled → delete the active session
 *   guard.isActive()           bool
 *   RecordingGuard.recover(slug, cb)   run recovery manually (autoRecover does this on construct)
 *   RecordingGuard.isSupported()
 *
 * Storage is shared across every tool in one DB; sessions are namespaced by slug
 * so /multi-track never surfaces /audio-logger's unfinished take.
 */
(function (global) {
    'use strict';

    var DB_NAME = 'timbrica_rec_guard';
    var DB_VERSION = 1;
    var CHUNK_STORE = 'chunks';   // key: sessionId + '::' + zero-padded idx  → Blob
    var META_STORE = 'sessions';  // key: sessionId → { id, slug, mime, state, startedAt, updatedAt, chunkCount, bytes, meta }
    var DEFAULT_TIMESLICE = 1000;
    var DEFAULT_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
    var _dbPromise = null;

    function isSupported() {
        return typeof global.indexedDB !== 'undefined' &&
               typeof global.MediaRecorder !== 'undefined';
    }

    // Page-unload latch. CRITICAL: a stop triggered by the page going away must
    // NOT be treated as a clean completion. Many tools stop the recorder in a
    // pagehide/beforeunload path — MicManager, for one, stops the mic tracks on
    // its own beforeunload, which makes MediaRecorder fire `onstop`, whose
    // handler typically calls guard.finish(). If finish() deleted the session
    // there, the crash-recovery data would be wiped at the exact moment the user
    // leaves the page — defeating the entire purpose. So once the page is
    // unloading, finish()/discardActive() keep the persisted take instead of
    // deleting it: an interrupted recording stays recoverable on the next visit.
    var _unloading = false;
    var _unloadLatchInstalled = false;
    function installUnloadLatch() {
        if (_unloadLatchInstalled || typeof global.addEventListener !== 'function') return;
        _unloadLatchInstalled = true;
        var set = function () { _unloading = true; };
        global.addEventListener('pagehide', set);
        global.addEventListener('beforeunload', set);
        // If the navigation is cancelled (user stays), clear the latch on the
        // next visible tick so a later genuine finish() still cleans up.
        global.addEventListener('visibilitychange', function () {
            if (global.document && global.document.visibilityState === 'visible') _unloading = false;
        });
    }

    function openDB() {
        if (_dbPromise) return _dbPromise;
        _dbPromise = new Promise(function (resolve, reject) {
            var req = global.indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = function () {
                var db = req.result;
                if (!db.objectStoreNames.contains(CHUNK_STORE)) db.createObjectStore(CHUNK_STORE);
                if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
            };
            req.onsuccess = function () { resolve(req.result); };
            req.onerror = function () { reject(req.error || new Error('IDB open failed')); };
            req.onblocked = function () { reject(new Error('IDB blocked')); };
        });
        return _dbPromise;
    }

    // Small promisified helpers. Each is its own short-lived transaction so a
    // write commits (durable) the moment it goes idle — that is what makes a
    // chunk survive a hard navigation that follows microseconds later.
    function idbPut(store, key, val) {
        return openDB().then(function (db) {
            return new Promise(function (res, rej) {
                var tx = db.transaction(store, 'readwrite');
                tx.objectStore(store).put(val, key);
                tx.oncomplete = function () { res(); };
                tx.onerror = function () { rej(tx.error); };
                tx.onabort = function () { rej(tx.error); };
            });
        });
    }
    function idbGet(store, key) {
        return openDB().then(function (db) {
            return new Promise(function (res, rej) {
                var tx = db.transaction(store, 'readonly');
                var r = tx.objectStore(store).get(key);
                r.onsuccess = function () { res(r.result); };
                r.onerror = function () { rej(r.error); };
            });
        });
    }
    function idbGetAll(store) {
        return openDB().then(function (db) {
            return new Promise(function (res, rej) {
                var tx = db.transaction(store, 'readonly');
                var s = tx.objectStore(store);
                var kr = s.getAllKeys(), vr = s.getAll(), keys, vals;
                kr.onsuccess = function () { keys = kr.result; };
                vr.onsuccess = function () { vals = vr.result; };
                tx.oncomplete = function () { res({ keys: keys || [], vals: vals || [] }); };
                tx.onerror = function () { rej(tx.error); };
            });
        });
    }
    // Delete every chunk of a session + its meta record, in one transaction.
    function idbDeleteSession(sessionId) {
        return openDB().then(function (db) {
            return new Promise(function (res, rej) {
                var tx = db.transaction([CHUNK_STORE, META_STORE], 'readwrite');
                var cs = tx.objectStore(CHUNK_STORE);
                var range = global.IDBKeyRange.bound(sessionId + '::', sessionId + '::\uffff');
                var delReq = cs.openKeyCursor(range);
                delReq.onsuccess = function (e) {
                    var cur = e.target.result;
                    if (cur) { cs.delete(cur.key); cur.continue(); }
                };
                tx.objectStore(META_STORE).delete(sessionId);
                tx.oncomplete = function () { res(); };
                tx.onerror = function () { rej(tx.error); };
                tx.onabort = function () { rej(tx.error); };
            });
        });
    }

    function pad(n) { var s = '' + n; while (s.length < 6) s = '0' + s; return s; }

    function uuid() {
        try {
            if (global.crypto && global.crypto.randomUUID) return global.crypto.randomUUID();
        } catch (_) {}
        // Fallback — no Math.random dependency needed for uniqueness here:
        // performance.now() + a monotonic counter is plenty for a per-tab id.
        _uidCounter = (_uidCounter + 1) % 1e6;
        var t = (global.performance && global.performance.now ? global.performance.now() : Date.now());
        return 'rg-' + Math.floor(t * 1000).toString(36) + '-' + _uidCounter.toString(36);
    }
    var _uidCounter = 0;

    function tel(type, payload) {
        try { if (global.__ae) global.__ae(type, payload || {}); } catch (_) {}
    }

    // ── localized copy for the default recovery banner ─────────────────────
    // (Only the recovery banner needs words; the beforeunload prompt uses the
    //  browser's own native "Leave site?" dialog — modern browsers ignore any
    //  custom string there, so no translation is required for it.)
    var BANNER = {
        en: { msg: 'We recovered an unfinished recording from your last visit.', dl: 'Download', discard: 'Discard' },
        ru: { msg: 'Мы восстановили незавершённую запись с прошлого раза.', dl: 'Скачать', discard: 'Удалить' },
        ko: { msg: '지난 방문에서 완료되지 않은 녹음을 복구했습니다.', dl: '다운로드', discard: '삭제' },
        ar: { msg: 'استعدنا تسجيلاً غير مكتمل من زيارتك السابقة.', dl: 'تنزيل', discard: 'حذف' },
        id: { msg: 'Kami memulihkan rekaman yang belum selesai dari kunjungan terakhir.', dl: 'Unduh', discard: 'Hapus' },
        pt: { msg: 'Recuperamos uma gravação não finalizada da sua última visita.', dl: 'Baixar', discard: 'Descartar' },
        de: { msg: 'Wir haben eine unfertige Aufnahme vom letzten Besuch wiederhergestellt.', dl: 'Herunterladen', discard: 'Verwerfen' },
        es: { msg: 'Recuperamos una grabación sin terminar de tu última visita.', dl: 'Descargar', discard: 'Descartar' },
        fr: { msg: 'Nous avons récupéré un enregistrement inachevé de votre dernière visite.', dl: 'Télécharger', discard: 'Supprimer' },
        uk: { msg: 'Ми відновили незавершений запис із минулого разу.', dl: 'Завантажити', discard: 'Видалити' },
        tr: { msg: 'Önceki ziyaretinizden yarım kalmış bir kaydı kurtardık.', dl: 'İndir', discard: 'Sil' }
    };
    function loc() {
        var l = (global.__LOCALE || 'en').toLowerCase().split('-')[0];
        return BANNER[l] || BANNER.en;
    }
    function extFor(mime) {
        mime = (mime || '').toLowerCase();
        if (mime.indexOf('mp4') !== -1 || mime.indexOf('m4a') !== -1 || mime.indexOf('aac') !== -1) return 'm4a';
        if (mime.indexOf('ogg') !== -1) return 'ogg';
        if (mime.indexOf('wav') !== -1) return 'wav';
        if (mime.indexOf('mpeg') !== -1 || mime.indexOf('mp3') !== -1) return 'mp3';
        if (mime.indexOf('video') !== -1 || (mime.indexOf('webm') !== -1 && mime.indexOf('vp') !== -1)) return 'webm';
        return 'webm';
    }

    // ── the guard ──────────────────────────────────────────────────────────
    function RecordingGuard(opts) {
        opts = opts || {};
        if (!opts.slug) throw new Error('RecordingGuard: slug is required');
        this.slug = opts.slug;
        this.timesliceMs = opts.timesliceMs || DEFAULT_TIMESLICE;
        this._warnOnUnload = opts.warnOnUnload !== false;
        this._maxAgeMs = opts.maxAgeMs || DEFAULT_MAX_AGE;
        this._onRecover = typeof opts.onRecover === 'function' ? opts.onRecover : null;
        this._session = null;   // { id, mime, idx, startedAt } while active
        this._recorder = null;
        this._writeChain = Promise.resolve();  // serialize writes to keep chunk order + backpressure
        this._boundUnload = null;
        this._boundHide = null;

        if (!isSupported()) {
            // Persistence unavailable — the tool still works, just without crash
            // recovery. Keep the API callable as no-ops.
            this._disabled = true;
            return;
        }
        if (opts.autoRecover !== false) {
            var self = this;
            // Defer so the tool finishes wiring (and __toast exists) first.
            setTimeout(function () { self._runRecovery(); }, 0);
        }
    }

    RecordingGuard.prototype.isActive = function () { return !!this._session; };

    RecordingGuard.prototype.begin = function (meta) {
        if (this._disabled) return null;
        installUnloadLatch();
        meta = meta || {};
        var id = uuid();
        this._session = {
            id: id,
            mime: meta.mimeType || meta.mime || 'audio/webm',
            idx: 0,
            startedAt: Date.now()
        };
        var rec = {
            id: id,
            slug: this.slug,
            mime: this._session.mime,
            state: 'recording',
            startedAt: this._session.startedAt,
            updatedAt: this._session.startedAt,
            chunkCount: 0,
            bytes: 0,
            meta: sanitizeMeta(meta)
        };
        this._enqueue(idbPut(META_STORE, id, rec));
        this._registerListeners();
        tel('feature_use', { f: 'rec_guard_begin', slug: this.slug });
        return id;
    };

    // Persist one chunk. Cheap (B1): one small record, no re-concat. Writes are
    // chained so ordering + durability hold even if ondataavailable outpaces IDB.
    RecordingGuard.prototype.pushChunk = function (blob) {
        if (this._disabled || !this._session || !blob || !blob.size) return;
        var s = this._session;
        var key = s.id + '::' + pad(s.idx);
        s.idx++;
        var self = this;
        this._enqueue(idbPut(CHUNK_STORE, key, blob).then(function () {
            // Refresh meta occasionally (not every chunk) — meta only needs to be
            // roughly current; the chunks themselves are the durable payload.
            if (s.idx === 1 || s.idx % 5 === 0) {
                return idbGet(META_STORE, s.id).then(function (m) {
                    if (!m) return;
                    m.chunkCount = s.idx;
                    m.updatedAt = Date.now();
                    return idbPut(META_STORE, s.id, m);
                });
            }
        }).catch(function (err) {
            tel('tool_error', { error_type: 'rec_guard_persist_fail', slug: self.slug, err: String(err && err.message || err).slice(0, 160) });
        }));
    };

    RecordingGuard.prototype.attachRecorder = function (mr) { this._recorder = mr || null; };

    // Clean completion — the take is safely in the tool's hands, so the session
    // is no longer "unfinished". Deletes chunks + meta — UNLESS the page is
    // unloading, in which case this "completion" is really an unload-triggered
    // recorder stop (e.g. MicManager stopping tracks on its beforeunload) and we
    // must KEEP the take so it survives as a recoverable interrupted recording.
    RecordingGuard.prototype.finish = function () {
        if (this._disabled || !this._session) return Promise.resolve();
        var id = this._session.id;
        this._teardown();
        if (_unloading) {
            tel('feature_use', { f: 'rec_guard_finish_kept_on_unload', slug: this.slug });
            return Promise.resolve();
        }
        tel('feature_use', { f: 'rec_guard_finish', slug: this.slug });
        return idbDeleteSession(id).catch(function () {});
    };

    // User cancelled the recording — same as finish (nothing worth keeping).
    // Also suppressed during unload for the same reason as finish().
    RecordingGuard.prototype.discardActive = function () {
        if (this._disabled || !this._session) return Promise.resolve();
        var id = this._session.id;
        this._teardown();
        if (_unloading) return Promise.resolve();
        return idbDeleteSession(id).catch(function () {});
    };

    RecordingGuard.prototype._teardown = function () {
        this._session = null;
        this._recorder = null;
        this._unregisterListeners();
    };

    RecordingGuard.prototype._enqueue = function (p) {
        this._writeChain = this._writeChain.then(function () { return p; }, function () { return p; });
        return this._writeChain;
    };

    // ── unload / visibility wiring ─────────────────────────────────────────
    RecordingGuard.prototype._registerListeners = function () {
        var self = this;
        if (this._warnOnUnload && !this._boundUnload) {
            this._boundUnload = function (e) {
                if (!self._session) return;
                // Modern browsers show their own generic "Leave site?" prompt;
                // the string is ignored but preventDefault/returnValue triggers it.
                e.preventDefault();
                e.returnValue = '';
                return '';
            };
            global.addEventListener('beforeunload', this._boundUnload);
        }
        if (!this._boundHide) {
            // On tab-hide (the reliable mobile-safe signal that fires BEFORE
            // pagehide, while the page can still run async IDB writes), ask the
            // recorder to flush its in-flight partial chunk. This shrinks the
            // worst-case lost tail from ~1 timeslice to ~nothing.
            this._boundHide = function () {
                if (!self._session || !self._recorder) return;
                try {
                    if (self._recorder.state === 'recording' && self._recorder.requestData) {
                        self._recorder.requestData();
                    }
                } catch (_) {}
            };
            global.addEventListener('visibilitychange', function () {
                if (global.document && global.document.visibilityState === 'hidden') self._boundHide();
            });
            global.addEventListener('pagehide', this._boundHide);
        }
    };
    RecordingGuard.prototype._unregisterListeners = function () {
        if (this._boundUnload) { global.removeEventListener('beforeunload', this._boundUnload); this._boundUnload = null; }
        // visibilitychange/pagehide taps are cheap and self-guard on _session,
        // so we leave them; they no-op once the session is cleared.
    };

    // ── recovery ───────────────────────────────────────────────────────────
    RecordingGuard.prototype._runRecovery = function () {
        var self = this;
        RecordingGuard.recover(this.slug, function (session) {
            if (self._onRecover) {
                var suppressed = false;
                try { suppressed = self._onRecover(session) === true; } catch (_) {}
                if (suppressed) return;
            }
            self._defaultBanner(session);
        }, this._maxAgeMs);
    };

    RecordingGuard.prototype._defaultBanner = function (session) {
        var L = loc();
        if (typeof global.__toast !== 'function') {
            // No toast primitive on the page — fall back to a console note; the
            // session stays in IDB so it isn't lost, just not surfaced.
            try { console.info('[RecordingGuard]', L.msg, '(' + Math.round(session.sizeBytes / 1024) + ' KB)'); } catch (_) {}
            return;
        }
        // __toast supports ONE action button + a close (X). We use the action for
        // the valuable path — Download (which saves + clears the session).
        // Dismissing via X is non-destructive: the session stays in IDB and is
        // offered again on the next visit until it ages out (maxAgeMs). There is
        // no explicit Discard in the default banner because the toast primitive
        // renders a single action; tools that want richer accept/discard UX pass
        // onRecover and render their own restore bar.
        global.__toast(L.msg, 'info', {
            action: {
                label: L.dl,
                onclick: function () {
                    var name = downloadName(session);
                    try {
                        if (global.__downloadFile) global.__downloadFile(session.blob, name);
                        else fallbackDownload(session.blob, name);
                    } catch (_) {}
                    session.accept();
                    tel('feature_use', { f: 'rec_guard_recovered_download', slug: session.meta && session.meta.slug });
                }
            }
        });
    };

    function downloadName(session) {
        var label = (session.meta && (session.meta.label || session.meta.name)) || 'recovered-recording';
        label = String(label).replace(/[^\w.\- ]+/g, '').trim() || 'recovered-recording';
        return label + '.' + extFor(session.mime);
    }
    function fallbackDownload(blob, name) {
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a); a.click();
        setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    }

    /**
     * Scan for the most recent unfinished session for `slug`, rebuild its blob,
     * and invoke cb(session) with accept()/discard() controls. Stale sessions
     * (older than maxAgeMs) and other slugs' sessions are purged/ignored. Static
     * so tools can call it manually if they disable autoRecover.
     */
    RecordingGuard.recover = function (slug, cb, maxAgeMs) {
        if (!isSupported()) return Promise.resolve(null);
        maxAgeMs = maxAgeMs || DEFAULT_MAX_AGE;
        var now = Date.now();
        return idbGetAll(META_STORE).then(function (metaAll) {
            var stale = [];
            var candidates = [];
            for (var i = 0; i < metaAll.vals.length; i++) {
                var m = metaAll.vals[i];
                if (!m || !m.id) continue;
                if ((now - (m.updatedAt || m.startedAt || 0)) > maxAgeMs) { stale.push(m.id); continue; }
                if (m.slug !== slug) continue;
                if (m.state === 'done') { stale.push(m.id); continue; }
                candidates.push(m);
            }
            // Purge anything stale (any slug) so forgotten takes don't accrete.
            stale.forEach(function (id) { idbDeleteSession(id).catch(function () {}); });
            if (!candidates.length) return null;
            // Newest first.
            candidates.sort(function (a, b) { return (b.updatedAt || b.startedAt) - (a.updatedAt || a.startedAt); });
            var m = candidates[0];
            // Also drop any OLDER same-slug candidates (only surface one).
            candidates.slice(1).forEach(function (o) { idbDeleteSession(o.id).catch(function () {}); });

            return idbGetAll(CHUNK_STORE).then(function (chunkAll) {
                var prefix = m.id + '::';
                var pairs = [];
                for (var j = 0; j < chunkAll.keys.length; j++) {
                    var k = chunkAll.keys[j];
                    if (typeof k === 'string' && k.indexOf(prefix) === 0) pairs.push({ k: k, v: chunkAll.vals[j] });
                }
                pairs.sort(function (a, b) { return a.k < b.k ? -1 : a.k > b.k ? 1 : 0; });
                var blob = new Blob(pairs.map(function (p) { return p.v; }), { type: m.mime || 'audio/webm' });
                if (!blob.size) { idbDeleteSession(m.id).catch(function () {}); return null; }

                var session = {
                    id: m.id,
                    blob: blob,
                    mime: m.mime,
                    meta: m.meta || {},
                    sizeBytes: blob.size,
                    chunkCount: pairs.length,
                    ageMs: now - (m.startedAt || now),
                    accept: function () { return idbDeleteSession(m.id).catch(function () {}); },
                    discard: function () { return idbDeleteSession(m.id).catch(function () {}); }
                };
                session.meta.slug = slug;
                tel('feature_use', { f: 'rec_guard_recovery_offered', slug: slug, sizeKB: Math.round(blob.size / 1024), chunks: pairs.length });
                try { cb(session); } catch (_) {}
                return session;
            });
        }).catch(function () { return null; });
    };

    function sanitizeMeta(meta) {
        var out = {};
        for (var k in meta) {
            if (!meta.hasOwnProperty(k)) continue;
            var v = meta[k];
            var t = typeof v;
            if (t === 'string' || t === 'number' || t === 'boolean') out[k] = v;
        }
        return out;
    }

    RecordingGuard.isSupported = isSupported;
    RecordingGuard._DB_NAME = DB_NAME;      // tests
    RecordingGuard.version = '1.0.0';

    global.RecordingGuard = RecordingGuard;
    if (typeof global.__drainSharedModule === 'function') global.__drainSharedModule('RecordingGuard');

})(typeof window !== 'undefined' ? window : this);
